import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { User, ArrowLeft, Loader2, Download, Trash2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/employee/details`;

function ViewDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);


  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      setUser(JSON.parse(storedUser));
      console.log("Current User:", JSON.parse(storedUser));
    }
  }, []);

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        const res = await axios.get(`${API}/${id}`);
        setEmployee(res.data.data);
        console.log(res.data.data["salary_slips"])
      } catch (err) {
        console.error(err);
        toast.error("Failed to load employee details");
      } finally {
        setIsLoading(false);
      }
    };

    fetchDetails();
  }, [id]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-[70vh]">
        <Loader2 className="animate-spin text-blue-500 mr-2" size={24} />
        <p className="text-gray-600 text-lg">Loading details...</p>
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="flex justify-center items-center h-[70vh]">
        <p className="text-red-500 text-lg font-medium">
          Employee details not found.
        </p>
      </div>
    );
  }

  // ✅ Handle download click (currently prints key)
  const handleDownload = async (label, key) => {
    if (!key) {
      toast.warning(`${label} not uploaded`);
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${BACKEND_URL}/api/employee/download-file`, {
        params: { file_key: key },
        headers: { Authorization: `Bearer ${token}` },
      });

      const downloadUrl = res.data.url;
      window.open(downloadUrl, "_blank"); // open in new tab
    } catch (err) {
      console.error("❌ Download error:", err);
      toast.error("Failed to generate download link");
    }
  };

  const handleDeleteSalarySlip = async (month) => {
    try {
      const token = localStorage.getItem("token");

      await axios.delete(
        `${BACKEND_URL}/api/employee/delete-salary-slip`,
        {
          params: {
            employee_id: id,
            month,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // remove deleted slip from UI instantly
      setEmployee((prev) => ({
        ...prev,
        salary_slips: Object.fromEntries(
          Object.entries(prev.salary_slips || {}).filter(
            ([m]) => m !== month
          )
        ),
      }));

      toast.success("Salary slip deleted successfully");
    } catch (err) {
      console.error("❌ Delete error:", err);
      toast.error("Failed to delete salary slip");
    }
  };

  const formatMonthLabel = (value) => {
    // "2025-10" -> "October 2025"
    if (!value) return "";
    const [year, month] = value.split("-");
    const date = new Date(Number(year), Number(month) - 1, 1);
    return date.toLocaleString("default", { month: "long", year: "numeric" });
  };



  return (
    <div className="w-full bg-gradient-to-br from-white via-slate-50 to-blue-50 min-h-screen p-10">
      {/* Header */}
      <div className="flex items-center justify-between mb-10 border-b border-gray-200 pb-5">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-full">
            <User className="text-blue-600" size={28} />
          </div>
          <div>
            <h2 className="text-3xl font-bold text-gray-800">
              {employee.full_name}
            </h2>
            <p className="text-gray-500 text-sm tracking-wide">
              Employee Profile Overview
            </p>
          </div>
        </div>

        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg text-sm font-medium transition"
        >
          <ArrowLeft size={18} /> Back
        </button>
      </div>

      {/* Grid Sections */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        <SectionCard title="Personal Information">
          <Info label="Date of Birth" value={employee.dob?.split("T")[0]} />
          <Info label="Gender" value={employee.gender} />
          <Info label="Marital Status" value={employee.marital_status} />
          <Info label="Blood Group" value={employee.blood_group} />
          <Info label="Personal Phone" value={employee.personal_phone_number} />
          <Info
            label="Alternate Phone"
            value={employee.alternate_phone_number}
          />
          <Info label="Personal Email" value={employee.personal_email_id} />
        </SectionCard>

        <SectionCard title="Emergency Contact">
          <Info label="Name" value={employee.emergency_contact_name} />
          <Info label="Phone" value={employee.emergency_contact_number} />
          <Info label="Address" value={employee.current_residential_address} />
        </SectionCard>

        <SectionCard title="Employment Details">
          <Info
            label="Joining Date"
            value={employee.joining_date?.split("T")[0]}
          />
          <Info label="Designation" value={employee.designation} />
          <Info label="Department" value={employee.department} />
          <Info label="Account Holder" value={employee.account_holder_name} />
          <Info label="Bank Name" value={employee.bank_name} />
          <Info label="Account Number" value={employee.account_number} />
          <Info label="IFSC Code" value={employee.ifsc_code} />
          <Info label="PAN Number" value={employee.pan_number} />
        </SectionCard>

        <SectionCard title="Education & Previous Employment">
          <Info
            label="Highest Qualification"
            value={employee.highest_qualification}
          />
          <Info
            label="University / College"
            value={employee.university_college_name}
          />
          <Info label="Graduation Year" value={employee.year_of_graduation} />
          <Info label="Last Company" value={employee.last_company} />
          <Info label="Last Designation" value={employee.last_designation} />
          <Info
            label="Employment Duration"
            value={employee.duration_of_employment}
          />
          <Info
            label="Reason for Leaving"
            value={employee.reason_for_leaving}
          />
        </SectionCard>

        {/* ✅ New Section — Uploaded Documents */}
        <SectionCard title="Uploaded Documents">
          <DocItem
            label="PAN Card"
            s3Key={employee.pan_card_doc}
            onDownload={() => handleDownload("PAN Card", employee.pan_card_doc)}
          />
          <DocItem
            label="Aadhaar Card"
            s3Key={employee.aadhar_card_doc}
            onDownload={() =>
              handleDownload("Aadhaar Card", employee.aadhar_card_doc)
            }
          />
          <DocItem
            label="Experience Letter"
            s3Key={employee.relieving_experience_letter}
            onDownload={() =>
              handleDownload(
                "Experience Letter",
                employee.relieving_experience_letter
              )
            }
          />
          <DocItem
            label="Bank Proof"
            s3Key={employee.cancelled_cheque}
            onDownload={() =>
              handleDownload("Bank Proof", employee.cancelled_cheque)
            }
          />
        </SectionCard>
        {/* ✅ New Section — Uploaded Documents */}
        <SectionCard title="Salary Slips">
          {employee.salary_slips &&
            Object.entries(employee.salary_slips).map(([month, s3Key]) => (
              <DocItem
                key={month}
                label={`Salary Slip - ${formatMonthLabel(month)}`}
                s3Key={s3Key}
                isAdmin={user?.role === "admin"}
                onDownload={() =>
                  handleDownload(
                    `Salary Slip - ${formatMonthLabel(month)}`,
                    s3Key
                  )
                }
                onDelete={() => handleDeleteSalarySlip(month)}
              />
            ))}
        </SectionCard>
      </div>
    </div>
  );
}

/* ✅ Section Card */
const SectionCard = ({ title, children }) => (
  <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-sm hover:shadow-md border border-gray-200 transition p-6">
    <h3 className="font-semibold text-gray-800 mb-4 text-lg flex items-center gap-2">
      <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
      {title}
    </h3>
    <div className="space-y-2">{children}</div>
  </div>
);

/* ✅ Info Item */
const Info = ({ label, value }) => (
  <div className="flex justify-between items-center border-b border-dashed border-gray-100 py-1">
    <span className="text-gray-600 font-medium">{label}</span>
    <span className="text-gray-900 font-semibold text-right break-words max-w-[60%]">
      {value || "—"}
    </span>
  </div>
);

/* ✅ Document Item (for S3 keys) */
const DocItem = ({
  label,
  s3Key,
  onDownload,
  onDelete,
  isAdmin,
}) => (
  <div className="flex justify-between items-center border-b border-dashed border-gray-100 py-1">
    <span className="text-gray-700 font-medium">{label}</span>

    <div className="flex items-center gap-3">
      <button
        onClick={onDownload}
        className="flex items-center gap-1 text-blue-600 hover:text-blue-800 text-sm font-medium transition"
      >
        <Download size={16} />
        {s3Key ? "Download" : "Not Uploaded"}
      </button>

      {isAdmin && s3Key && (
        <button
          onClick={onDelete}
          className="text-red-500 hover:text-red-700 transition"
        >
          <Trash2 size={16} />
        </button>
      )}
    </div>
  </div>
);

export default ViewDetails;
